import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { ANY_SPORT_MARKET_WATCHLISTS } from '@sorare/core/src/constants/__generated__/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import {
  sportAnalyticTag,
  useSportContext,
} from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { glossary } from '@sorare/core/src/lib/glossary';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { IosHeader } from '#src/components/market/IosHeader/index.tsx';
import { WatchlistEmptyState } from '#src/components/watchlists/WatchlistEmptyState/index.tsx';
import {
  getSubscribableWatchlistTypes,
  useHasFollowedPlayers,
} from '#src/hooks/useHasFollowedPlayers.ts';
import { SCOUTING_CENTER_CONFIG_BY_SPORT } from '#src/lib/scoutingCenterFilters.ts';
import { AdvancedPlayerSearch } from '#src/searchCommonPlayer/AdvancedPlayerSearch/index.tsx';

import { FavoritesHeader } from './FavoritesHeader/index.tsx';
import type {
  FavoritesWatchlistPageQuery,
  FavoritesWatchlistPageQueryVariables,
} from './__generated__/index.graphql.ts';

const FAVORITES_WATCHLIST_PAGE_QUERY = gql`
  query FavoritesWatchlistPageQuery(
    $sport: Sport!
    $types: [SubscribableType!]
  ) {
    so5 {
      so5Fixture(type: UPCOMING, eventType: CLASSIC, sport: $sport) {
        slug
      }
    }
    currentUser {
      slug
      ...useHasFollowedPlayers_currentUser
    }
  }
  ${useHasFollowedPlayers.fragments.currentUser}
` as TypedDocumentNode<
  FavoritesWatchlistPageQuery,
  FavoritesWatchlistPageQueryVariables
>;

export const FavoritesPage = () => {
  const { formatMessage } = useIntlContext();
  const { sport } = useSportContext();
  const { data, loading } = useQuery(FAVORITES_WATCHLIST_PAGE_QUERY, {
    variables: {
      sport,
      types: getSubscribableWatchlistTypes(sport),
    },
    nextFetchPolicy: 'network-only',
  });
  const { hasFollowedPlayers, followedPlayers, count } = useHasFollowedPlayers({
    currentUser: data?.currentUser,
  });

  const so5FixtureSlug = data?.so5?.so5Fixture?.slug;

  const { sorts, filters } = SCOUTING_CENTER_CONFIG_BY_SPORT[sport];

  return (
    <>
      <PageHeader
        text={formatMessage(glossary.favorites)}
        backTo={generateSportPath(ANY_SPORT_MARKET_WATCHLISTS, {
          sport,
        })}
        disableBackBrowser
      />
      <Vertical gap={0}>
        <IosHeader
          to={generateSportPath(ANY_SPORT_MARKET_WATCHLISTS, {
            sport,
          })}
          text={glossary.favorites}
        />
        <FavoritesHeader
          playerUrls={
            followedPlayers
              ?.map(
                player => 'fullPictureUrl' in player && player.fullPictureUrl
              )
              .filter(Boolean) || []
          }
          count={count}
        />
      </Vertical>
      {!!loading && <LoadingIndicator fullScreen />}
      {hasFollowedPlayers && !loading ? (
        <AdvancedPlayerSearch
          sport={sport}
          key="favorites"
          sorts={sorts}
          defaultSort={sorts[0]}
          cardFilters={filters}
          analyticsTags={['Watchlist', sportAnalyticTag[sport]]}
          topic={{ type: 'watchlist', label: '' }}
          attributesToRetrieve={['slug']}
          favoritesWatchlist
          so5FixtureSlug={so5FixtureSlug}
          withStartersOn={false}
          shouldSkip={loading}
        />
      ) : (
        <WatchlistEmptyState />
      )}
    </>
  );
};
